import { useCallback, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useIntentsPaged, useInteractionsForIntent } from "./data.ts";
import {
  buildFlowFromIntent,
  groupParallelRounds,
  COL_X,
  PROV_X,
  TILE,
  tileCX,
  tileCY,
  type FlowNode,
} from "./flow/flowData.ts";
import { usePlayback, prefersReducedMotion } from "./flow/usePlayback.ts";
import { TimelineRail } from "./flow/TimelineRail.tsx";
import { TraceInspector } from "./flow/TraceInspector.tsx";
import { DetailDrawer } from "./DetailDrawer.tsx";
import { brandIconFor } from "./AppIcon.tsx";
import "./agentdna.css";

const TRACE_TITLE_ID = "trace-inspector-title";

/**
 * The flow visualization page (`/intents/:intentId/flow`).
 *
 * The static, layered node-link SVG renders unconditionally for every intent with ≥1 hop
 * (Requirement 4.4) — the animation, timeline rail, and trace inspector layer ON TOP of it, driven
 * entirely by the browser-derived `usePlayback` state machine over the already-fetched read data
 * (Requirements 8.1, 8.2 — no persistence, no new writes). When an intent carries no rich step data
 * the page still renders the graph and the (non-animated) rail; no error state is shown.
 *
 * Playback controls are real labelled `<button>`s and every step is reachable through the rail +
 * inspector without animation (Requirements 11.1, 11.3). Reduced motion (`prefersReducedMotion`) is
 * threaded into `usePlayback` and `TimelineRail` so no continuous packet motion runs; the
 * active-round highlight uses only CSS transitions, which stay on.
 */
export function IntentFlowPage(): ReactNode {
  const { role } = useIdentity();
  const { intentId = "" } = useParams();
  const { data: intents, loading: intentsLoading } = useIntentsPaged(1);
  const { data: interactions, loading: ixnLoading } = useInteractionsForIntent(intentId);

  const intent = intents.items.find((x) => x.id === intentId) ?? null;
  const graph = useMemo(() => {
    if (intent === null) return null;
    return buildFlowFromIntent(
      intent.initiator.id,
      intent.initiator.name,
      interactions.items,
      intent.provenanceRecordID,
    );
  }, [intent, interactions.items]);

  // Reduced-motion preference, resolved once and threaded into playback + the rail so no continuous
  // packet motion runs; discrete highlight transitions remain available.
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);
  const playback = usePlayback(graph, { reducedMotion });

  // step index → round index, so selecting a rail row (or an active-step lookup) can map back to the
  // playback round. Pure derivation over `groupParallelRounds` — no persistence.
  const stepToRound = useMemo(() => {
    const map = new Map<number, number>();
    const rounds = groupParallelRounds(graph?.steps ?? []);
    rounds.forEach((round, roundIndex) => {
      for (const stepIndex of round) map.set(stepIndex, roundIndex);
    });
    return map;
  }, [graph]);

  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  // --- Draggable nodes (observability-ui follow-up) ------------------------------------------------
  // Per-node position OVERRIDES in SVG-viewBox coordinates, keyed by node id. Empty by default, so
  // the graph renders at its computed layout; the operator can drag any tile anywhere, and Reset
  // clears every override to restore the default layout. This is view-only ephemeral state — nothing
  // is persisted (consistent with the rest of this page).
  const svgRef = useRef<SVGSVGElement>(null);
  const [positions, setPositions] = useState<ReadonlyMap<string, { x: number; y: number }>>(new Map());
  // The in-flight drag: which node, and the pointer→node-origin offset in SVG units so the tile does
  // not jump to the cursor on grab.
  const dragRef = useRef<{ id: string; dx: number; dy: number } | null>(null);

  // Convert a pointer event's client coordinates into the SVG's viewBox coordinate space, so a drag
  // maps 1:1 to the graph regardless of how `preserveAspectRatio` has scaled the canvas.
  const toSvgPoint = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (svg === null) return null;
    const ctm = svg.getScreenCTM();
    if (ctm === null) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }, []);

  const resetLayout = useCallback((): void => setPositions(new Map()), []);

  const state = resolveState(role, intentsLoading || ixnLoading);
  const byId = new Map((graph?.nodes ?? []).map((n) => [n.id, n]));

  // Node labels for the rail (id → display label), falling back to the raw id for synthetic nodes.
  const nodeLabel = (id: string): string => byId.get(id)?.label ?? id;

  // The set of interaction ids lit by the active round, plus the involved node ids — used to add the
  // `--active` highlight to the matching SVG edges/nodes.
  const activeEdgeIds = new Set<string>();
  const activeNodeIds = new Set<string>();
  if (graph !== null) {
    for (const stepIndex of playback.activeSteps) {
      const step = graph.steps[stepIndex];
      if (step === undefined) continue;
      activeEdgeIds.add(step.interactionId);
      activeNodeIds.add(step.from);
      activeNodeIds.add(step.to);
    }
  }

  // The provenance node (synthetic) for the seal beat, placed one column past the last hop's target.
  const sealNode = graph !== null ? sealProvenanceNode(graph.nodes, graph.sealEdges) : null;

  // Resolve a node id to its CURRENT position: a drag override when present, else the node's computed
  // layout position (the real node, or the synthetic seal node). Used by both the edge math and the
  // tile render so a dragged node and its connected edges move together.
  const posOf = (id: string): { x: number; y: number } | null => {
    const override = positions.get(id);
    if (override !== undefined) return override;
    const node = byId.get(id);
    if (node !== undefined) return { x: node.x, y: node.y };
    if (sealNode !== null && sealNode.id === id) return { x: sealNode.x, y: sealNode.y };
    return null;
  };

  // Begin dragging a node: capture the pointer and record the grab offset in SVG units.
  const onNodePointerDown = (id: string) => (e: ReactPointerEvent<SVGGElement>): void => {
    const start = posOf(id);
    const local = toSvgPoint(e.clientX, e.clientY);
    if (start === null || local === null) return;
    dragRef.current = { id, dx: local.x - start.x, dy: local.y - start.y };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
  };

  const onNodePointerMove = (e: ReactPointerEvent<SVGGElement>): void => {
    const drag = dragRef.current;
    if (drag === null) return;
    const local = toSvgPoint(e.clientX, e.clientY);
    if (local === null) return;
    const next = { x: local.x - drag.dx, y: local.y - drag.dy };
    setPositions((prev) => {
      const copy = new Map(prev);
      copy.set(drag.id, next);
      return copy;
    });
  };

  const onNodePointerUp = (e: ReactPointerEvent<SVGGElement>): void => {
    if (dragRef.current === null) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer may already be released; ignore */
    }
    dragRef.current = null;
  };

  const selectStep = (stepIndex: number): void => {
    const round = stepToRound.get(stepIndex);
    if (round !== undefined) playback.jump(round);
    setSelectedStep(stepIndex);
  };

  const closeInspector = (): void => setSelectedStep(null);

  // Resolve the selected step for the inspector: the timed FlowStep plus its originating interaction
  // and that interaction's rich step (for payload fallback). All from read data.
  const selectedFlowStep = selectedStep !== null ? (graph?.steps[selectedStep] ?? null) : null;
  const selectedInteraction =
    selectedFlowStep !== null
      ? (interactions.items.find((i) => i.id === selectedFlowStep.interactionId) ?? undefined)
      : undefined;
  const selectedRichStep = selectedInteraction?.steps?.[0];

  // The current hop label for the bottom step bar ("<n>  from → to"), derived from the active round.
  // On the seal beat (no active steps) it reads "<n>  → Provenance Layer".
  const currentHopLabel = ((): string | null => {
    if (graph === null || playback.roundCount === 0) return null;
    const num = String(playback.round + 1).padStart(2, "0");
    const firstStepIdx = playback.activeSteps[0];
    if (firstStepIdx === undefined) {
      // Seal beat: point at the provenance node.
      const seal = graph.sealEdges[0];
      const to = seal !== undefined ? nodeLabel(seal.to) : "Provenance Layer";
      return `${num}  → ${to}`;
    }
    const step = graph.steps[firstStepIdx];
    if (step === undefined) return num;
    return `${num}  ${nodeLabel(step.from)} → ${nodeLabel(step.to)}`;
  })();

  return (
    <ScreenScaffold
      title={intent !== null ? `${intent.name} · Flow` : "Flow"}
      subtitle="How this intent moved across the agent network."
      state={state}
    >
      <p className="adna-breadcrumb">
        <Link className="drawer-link" to={`/intents/${encodeURIComponent(intentId)}`}>
          ← Back to intent
        </Link>
      </p>

      {graph !== null && graph.edges.length === 0 && (
        <p className="adna-flow__empty">No interactions to visualize for this intent yet.</p>
      )}

      {graph !== null && graph.edges.length > 0 && (
        <div className="adna-flow-layout">
          <div className="adna-flow">
            {/* Reset the layout: clears every drag override so the tiles snap back to the default
                computed positions. Pinned top-right over the canvas. */}
            <button
              type="button"
              className="adna-flow__reset"
              onClick={resetLayout}
              disabled={positions.size === 0}
              title="Reset the view to its default layout"
            >
              Reset view
            </button>

            {/* In-canvas header pill (reference): live indicator + node/hop counts, top-left. */}
            <div className="adna-flow__trace-pill" aria-hidden="true">
              <span className="adna-flow__trace-dot" />
              <span className="adna-flow__trace-text">
                LIVE TRACE · {graph.nodes.length} node{graph.nodes.length === 1 ? "" : "s"} ·{" "}
                {graph.edges.length} hop{graph.edges.length === 1 ? "" : "s"}
              </span>
            </div>

            <svg
              ref={svgRef}
              className="adna-flow__svg"
              viewBox={`0 0 ${graph.width} ${graph.height}`}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`Flow graph for ${intent?.name ?? "intent"}: ${graph.nodes.length} nodes, ${graph.edges.length} interactions`}
            >
              <defs>
                <marker id="adna-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="var(--text-muted)" />
                </marker>
                <marker id="adna-arrow-threat" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="var(--status-error, #b42318)" />
                </marker>
                <marker id="adna-arrow-seal" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="var(--accent, #2563eb)" />
                </marker>
              </defs>

              {graph.edges.map((e) => {
                const a = posOf(e.from);
                const b = posOf(e.to);
                if (a === null || b === null) return null;
                // Connect at the tiles' horizontal mid-points (reference-style): leave the source
                // tile's right edge, enter the target tile's left edge, both at tile centre height.
                // Positions come from `posOf`, so a dragged node drags its edges with it.
                const x1 = tileCX(a.x) + TILE / 2;
                const y1 = tileCY(a.y);
                const x2 = tileCX(b.x) - TILE / 2;
                const y2 = tileCY(b.y);
                const mx = (x1 + x2) / 2;
                const active = activeEdgeIds.has(e.id);
                const d = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
                const cls = [
                  "adna-flow__edge",
                  e.threat ? "adna-flow__edge--threat" : "",
                  active ? "adna-flow__edge--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <g key={e.id}>
                    <path
                      d={d}
                      data-edge-id={e.id}
                      className={cls}
                      markerEnd={e.threat ? "url(#adna-arrow-threat)" : "url(#adna-arrow)"}
                    />
                    {/* Traveling packet (Requirement 12.3): a pulse that rides the edge path in the
                       hop's direction. Rendered ONLY for an active edge when reduced motion is off —
                       so under reduced motion no packet element exists at all (Requirement 12.5). */}
                    {active && !reducedMotion && (
                      <path
                        className={`adna-flow__packet${e.threat ? " adna-flow__packet--threat" : ""}`}
                        d="M -5 -4 L 6 0 L -5 4 Z"
                        aria-hidden="true"
                      >
                        <animateMotion dur="1.4s" repeatCount="indefinite" path={d} rotate="auto" />
                      </path>
                    )}
                  </g>
                );
              })}

              {sealNode !== null &&
                graph.sealEdges.map((seal) => {
                  const a = posOf(seal.from);
                  const sealPos = posOf(sealNode.id);
                  if (a === null || sealPos === null) return null;
                  const x1 = tileCX(a.x) + TILE / 2;
                  const y1 = tileCY(a.y);
                  const x2 = tileCX(sealPos.x) - TILE / 2;
                  const y2 = tileCY(sealPos.y);
                  const mx = (x1 + x2) / 2;
                  const d = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
                  const cls = [
                    "adna-flow__edge",
                    "adna-flow__edge--seal",
                    playback.sealActive ? "adna-flow__edge--active" : "",
                  ].join(" ");
                  return (
                    <g key={`${seal.from}->${seal.to}`}>
                      <path d={d} data-edge-id={`seal:${seal.from}->${seal.to}`} className={cls} markerEnd="url(#adna-arrow-seal)" />
                      {/* Seal-beat packet (Requirement 12.3): rides the provenance seal edge, only
                         while the seal beat is active and reduced motion is off (Requirement 12.5). */}
                      {playback.sealActive && !reducedMotion && (
                        <path className="adna-flow__packet adna-flow__packet--seal" d="M -5 -4 L 6 0 L -5 4 Z" aria-hidden="true">
                          <animateMotion dur="1.4s" repeatCount="indefinite" path={d} rotate="auto" />
                        </path>
                      )}
                    </g>
                  );
                })}

              {graph.nodes.map((n) => {
                const pos = posOf(n.id) ?? { x: n.x, y: n.y };
                return (
                  <NodeTile
                    key={n.id}
                    x={pos.x}
                    y={pos.y}
                    kind={n.kind}
                    label={n.label}
                    threat={n.threat}
                    brand={n.kind === "tool" ? brandIconFor(n.label) : null}
                    active={activeNodeIds.has(n.id)}
                    dragging={dragRef.current?.id === n.id}
                    onPointerDown={onNodePointerDown(n.id)}
                    onPointerMove={onNodePointerMove}
                    onPointerUp={onNodePointerUp}
                  />
                );
              })}

              {sealNode !== null && (() => {
                const pos = posOf(sealNode.id) ?? { x: sealNode.x, y: sealNode.y };
                return (
                  <NodeTile
                    x={pos.x}
                    y={pos.y}
                    kind="provenance"
                    label={sealNode.label}
                    threat={false}
                    brand={null}
                    active={playback.sealActive}
                    dragging={dragRef.current?.id === sealNode.id}
                    onPointerDown={onNodePointerDown(sealNode.id)}
                    onPointerMove={onNodePointerMove}
                    onPointerUp={onNodePointerUp}
                  />
                );
              })()}
            </svg>

            <ul className="adna-flow__legend" aria-label="Legend">
              <li><span className="adna-flow__swatch adna-flow__swatch--human" /> Owner</li>
              <li><span className="adna-flow__swatch adna-flow__swatch--agent" /> Agent</li>
              <li><span className="adna-flow__swatch adna-flow__swatch--tool" /> Tool / App</li>
              <li><span className="adna-flow__swatch adna-flow__swatch--threat" /> Threat</li>
            </ul>

            {/* Bottom step-status bar (reference): the current hop label on the left, playback
                controls + round counter on the right. Styled for the dark canvas. */}
            <div className="adna-flow__stepbar">
              <span className="adna-flow__step-label" aria-live="polite">
                {currentHopLabel ?? "No steps"}
              </span>
              <div className="adna-flow__stepbar-controls" role="group" aria-label="Playback controls">
                {/* Step-back: a "skip to previous" icon (bar + left triangle) — deliberately
                    distinct from the play/pause glyph so the two never read as the same control. */}
                <button
                  type="button"
                  className="adna-flow__ctl"
                  onClick={playback.stepBack}
                  aria-label="Step back one round"
                  title="Step back"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <rect x="5" y="5" width="2.6" height="14" rx="1" />
                    <path d="M20 5.5v13a1 1 0 0 1-1.54.84l-9-6.5a1 1 0 0 1 0-1.68l9-6.5A1 1 0 0 1 20 5.5z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="adna-flow__ctl adna-flow__ctl--primary"
                  onClick={() => (playback.playing ? playback.pause() : playback.play())}
                  aria-label={playback.playing ? "Pause playback" : "Play playback"}
                  aria-pressed={playback.playing}
                  disabled={reducedMotion}
                >
                  {playback.playing ? "⏸" : "▶"}
                </button>
                {/* Step-forward: a "skip to next" icon (right triangle + bar), the mirror of
                    step-back and clearly different from the play triangle. */}
                <button
                  type="button"
                  className="adna-flow__ctl"
                  onClick={playback.stepForward}
                  aria-label="Step forward one round"
                  title="Step forward"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d="M4 5.5v13a1 1 0 0 0 1.54.84l9-6.5a1 1 0 0 0 0-1.68l-9-6.5A1 1 0 0 0 4 5.5z" />
                    <rect x="16.4" y="5" width="2.6" height="14" rx="1" />
                  </svg>
                </button>
                <span className="adna-flow__round">
                  {playback.roundCount > 0
                    ? `${playback.round + 1} / ${playback.roundCount}`
                    : "0 / 0"}
                  {reducedMotion && " · reduced motion"}
                </span>
              </div>
            </div>
          </div>

          <aside className="adna-flow__rail" aria-label="Interaction timeline">
            <TimelineRail
              steps={graph.steps}
              nodeLabel={nodeLabel}
              activeSteps={playback.activeSteps}
              onSelectStep={selectStep}
              reducedMotion={reducedMotion}
            />
          </aside>
        </div>
      )}

      <DetailDrawer open={selectedStep !== null} onClose={closeInspector} labelledBy={TRACE_TITLE_ID}>
        {selectedFlowStep !== null && (
          <TraceInspector
            step={selectedRichStep}
            flowStep={selectedFlowStep}
            {...(selectedInteraction !== undefined ? { interaction: selectedInteraction } : {})}
            titleId={TRACE_TITLE_ID}
            onClose={closeInspector}
          />
        )}
      </DetailDrawer>
    </ScreenScaffold>
  );
}

/**
 * Place the synthetic provenance node for the seal beat: one column to the right of the last hop's
 * target node, aligned to its row. Returns `null` when there is no seal edge (no provenance / no
 * hops), so nothing seal-related renders — identical to today's behaviour.
 */
function sealProvenanceNode(
  nodes: readonly FlowNode[],
  sealEdges: readonly { readonly from: string; readonly to: string }[],
): FlowNode | null {
  const seal = sealEdges[0];
  if (seal === undefined) return null;
  const source = nodes.find((n) => n.id === seal.from);
  if (source === undefined) return null;
  // The seal node sits in its OWN lane to the right of the tool column (PROV_X) and ON THE SOURCE'S
  // ROW — not stacked a row below in a shared column — so its "Seal …" caption can never overlap the
  // label of the tile above it (the previous `source.y + ROW_GAP` placement caused that overlap).
  return {
    id: seal.to,
    label: seal.to.startsWith("prov-") ? `Seal ${seal.to.slice(5)}` : seal.to,
    kind: "provenance",
    col: COL_X.length,
    x: PROV_X,
    y: source.y,
    threat: false,
  };
}

/**
 * A node rendered the reference way: a centered rounded-square glyph TILE at the top of the node's
 * bounding box, with the label as text BENEATH it. The whole node keeps its NODE_W×NODE_H box for
 * layout/edge math; only the visual is the compact tile + caption. Tool tiles overlay their brand
 * icon; the provenance tile shows a shield. The `--active` class (set by the caller) drives the
 * highlight/glow from the animation layer.
 */
function NodeTile(props: {
  readonly x: number;
  readonly y: number;
  readonly kind: FlowNode["kind"];
  readonly label: string;
  readonly threat: boolean;
  readonly brand: string | null;
  readonly active: boolean;
  readonly dragging?: boolean;
  readonly onPointerDown?: (e: ReactPointerEvent<SVGGElement>) => void;
  readonly onPointerMove?: (e: ReactPointerEvent<SVGGElement>) => void;
  readonly onPointerUp?: (e: ReactPointerEvent<SVGGElement>) => void;
}): ReactNode {
  const { x, y, kind, label, threat, brand, active, dragging } = props;
  const cx = tileCX(x);
  const tileX = cx - TILE / 2;
  const tileY = y;
  const human = kind === "human";
  const prov = kind === "provenance";
  const fill = human
    ? "var(--accent, #2563eb)"
    : prov
      ? "var(--status-healthy, #16a34a)"
      : "var(--adna-canvas-tile, #16223a)";
  const stroke = threat
    ? "var(--status-error, #b42318)"
    : prov
      ? "var(--status-healthy, #16a34a)"
      : "var(--adna-canvas-border, #27354d)";
  const glyphColor = human || prov ? "var(--accent-contrast, #fff)" : "var(--adna-canvas-muted, #8aa0bd)";

  return (
    <g
      className={`adna-flow__node adna-flow__node--${kind}${active ? " adna-flow__node--active" : ""} adna-flow__node--draggable`}
      data-dragging={dragging ? "true" : undefined}
      onPointerDown={props.onPointerDown}
      onPointerMove={props.onPointerMove}
      onPointerUp={props.onPointerUp}
    >
      <rect
        className="adna-flow__tile"
        x={tileX}
        y={tileY}
        width={TILE}
        height={TILE}
        rx={12}
        fill={fill}
        stroke={stroke}
        strokeWidth={threat ? 2 : 1.5}
      />
      {brand !== null ? (
        <image
          href={brand}
          x={tileX + 10}
          y={tileY + 10}
          width={TILE - 20}
          height={TILE - 20}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <g
          transform={`translate(${tileX + TILE / 2 - 10} ${tileY + TILE / 2 - 10})`}
          fill="none"
          stroke={glyphColor}
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {nodeGlyph(kind)}
        </g>
      )}
      <text className="adna-flow__node-label" x={cx} y={tileY + TILE + 16} textAnchor="middle">
        {clip(label, 16)}
      </text>
    </g>
  );
}

/** A 20×20 stroke glyph per node kind (human / agent / tool / provenance shield). */
function nodeGlyph(kind: FlowNode["kind"]): ReactNode {
  switch (kind) {
    case "human":
      return <><circle cx="10" cy="7" r="3.2" /><path d="M4 18c0-3.3 2.7-5 6-5s6 1.7 6 5" /></>;
    case "agent":
      return <><rect x="4" y="6" width="12" height="10" rx="2" /><path d="M10 3v3M7 10h.01M13 10h.01M8 13h4" /></>;
    case "provenance":
      return <><path d="M10 2l6 2.5v4C16 13 13.4 15.5 10 17 6.6 15.5 4 13 4 8.5v-4L10 2z" /><path d="M7.5 9.5l1.8 1.8 3.2-3.6" /></>;
    case "tool":
    default:
      return <><path d="M13.5 4.5a3.5 3.5 0 0 0-4.6 4.6l-5 5 1.9 1.9 5-5a3.5 3.5 0 0 0 4.6-4.6l-2 2-1.9-.4-.4-1.9 2-2z" /></>;
  }
}

function clip(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function resolveState(role: ReturnType<typeof useIdentity>["role"], loading: boolean): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Intents need at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  return { kind: "ready" };
}
